import { useMemo, useState } from "react";
import styles from "./PoliciesView.module.scss";
import { Pagination, View, ViewHeader } from "@/components";
import type { PolicyCategoryFilter, PolicyScopeFilter } from "../../_constants/policies-toolbar.const";
import {
  DEVICE_PROFILE_PROTOTYPE_SAMPLES,
  createPrototypeProfileId,
} from "../../_constants/device-profile-prototypes.const";
import type { ApiDeviceProfile } from "../../_types/device-profiles.types";
import { CreatePolicyDrawer } from "../create-policy-drawer";
import { DeleteProfileModal } from "../delete-profile-modal";
import { DeviceProfilesList } from "../device-profiles-list";
import { EditPolicyDrawer } from "../edit-policy-drawer";
import { PolicyDetailDrawer } from "../policy-detail-drawer";
import { PoliciesToolbar } from "../policies-toolbar";

export const PoliciesView = () => {
  const [items, setItems] = useState<ApiDeviceProfile[]>(() =>
    DEVICE_PROFILE_PROTOTYPE_SAMPLES.map((p) => ({
      ...p,
      config: { ...p.config },
    })),
  );
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [scope, setScope] = useState<PolicyScopeFilter>("all");
  const [category, setCategory] = useState<PolicyCategoryFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editProfile, setEditProfile] = useState<ApiDeviceProfile | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ApiDeviceProfile | null>(null);

  const totalPages = Math.max(1, Math.ceil(items.length / limit) || 1);
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * limit;
    return items.slice(start, start + limit);
  }, [items, safePage, limit]);

  const openDetail = (id: string) => {
    setSelectedId(id);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
  };

  const openEdit = (p: ApiDeviceProfile) => {
    setEditProfile(p);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditProfile(null);
  };

  const duplicateProfile = (p: ApiDeviceProfile) => {
    const now = Date.now();
    setItems((prev) => [
      {
        ...p,
        id: createPrototypeProfileId(),
        name: `${p.name} (copy)`,
        config: JSON.parse(JSON.stringify(p.config)) as Record<string, unknown>,
        configVersion: 1,
        createdAt: now,
        updatedAt: now,
      },
      ...prev,
    ]);
  };

  const confirmDeleteProfile = () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setItems((prev) => prev.filter((p) => p.id !== id));
    setPendingDelete(null);
    if (selectedId === id) closeDetail();
  };

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="Profiles (Prototype / Preview)"
          description="UI-only preview of device behavior templates. Sample data lives in component memory and resets on refresh.<br />Create, edit, and details stay local — no API mutations, and prototypes cannot be assigned to real devices."
        />

        <PoliciesToolbar
          scope={scope}
          category={category}
          onScopeChange={setScope}
          onCategoryChange={setCategory}
          onCreatePolicyClick={() => setCreateOpen(true)}
        />

        <DeviceProfilesList
          items={pageItems}
          isLoading={false}
          isFetching={false}
          isError={false}
          onRetry={() => {}}
          onRowClick={openDetail}
          onEdit={openEdit}
          onDuplicate={duplicateProfile}
          onRequestDelete={(p) => setPendingDelete(p)}
          actionsDisabled={false}
        />

        {items.length > 0 ? (
          <Pagination
            page={safePage}
            limit={limit}
            total={items.length}
            totalPages={totalPages}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setLimit(next);
              setPage(1);
            }}
          />
        ) : null}

        <CreatePolicyDrawer
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={(profile) => setItems((prev) => [profile, ...prev])}
        />

        <EditPolicyDrawer
          isOpen={editOpen}
          profile={editProfile}
          onClose={closeEdit}
          onSave={(profile) =>
            setItems((prev) => prev.map((p) => (p.id === profile.id ? profile : p)))
          }
        />

        <DeleteProfileModal
          isOpen={pendingDelete !== null}
          profileName={pendingDelete?.name ?? ""}
          onClose={() => setPendingDelete(null)}
          onConfirm={confirmDeleteProfile}
          isDeleting={false}
        />

        <PolicyDetailDrawer
          policyId={selectedId}
          policies={items}
          isOpen={detailOpen}
          onClose={closeDetail}
          onRequestDelete={(p) => setPendingDelete(p)}
        />
      </div>
    </View>
  );
};
